/**
 * 📋 SDLC 6.3.1 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/hooks/useOrganizationManagement.ts
 * PURPOSE: Custom hook for Organization Management with ADR-028 8-Layer Hierarchy
 * SDLC FRAMEWORK VERSION: 6.1.1 (7-Pillar + 3-Section + Long-Running Agent Protocol)
 * PILLAR: P1 (Stage 06 - BUILD) + P6 (React Best Practices)
 * SPRINT GOVERNANCE: Rule 9: Documentation Standards
 *
 * DATE: 2026-01-19
 * DESIGN: docs/02-design/04-ui-ux-design/ORGANIZATION-MANAGEMENT-UI-DESIGN.md
 * APPROVED: Sprint 27 - Pilot Onboarding
 * COMPLIANCE: SDLC 6.3.1 + ADR-028 Organization Entity Schema
 *
 * @business-context
 * - Domain: Vietnamese F&B Workforce Management - Organization Hierarchy
 * - Impact: State management for 8-Layer hierarchy CRUD operations
 * - Stakeholders: Frontend Team
 *
 * @technical-architecture
 * - Layer: Business Logic Layer
 * - Pattern: Custom React Hook, State Management
 * - Dependencies: React, organizationManagementApi
 *
 * @cross-references
 * - ADR: docs/02-design/18-adrs/ADR-028-organization-entity-schema-standardization.md
 * - API Service: frontend/src/services/api/organizationManagement.ts
 * - Types: frontend/src/types/organization.ts
 */

import { useState, useEffect, useCallback } from 'react';
import {
  OrganizationEntity,
  OrganizationCreate,
  OrganizationUpdate,
  OrganizationFilters,
  OrganizationType,
} from '@/types/organization';
import { organizationManagementApi } from '@/services/api/organizationManagement';

/**
 * State interface for organization management
 */
interface OrganizationManagementState {
  organizations: OrganizationEntity[];
  loading: boolean;
  error: string | null;
  totalCount: number;
}

/**
 * Return type for useOrganizationManagement hook
 */
interface UseOrganizationManagementReturn extends OrganizationManagementState {
  // CRUD operations
  fetchOrganizations: (filters?: OrganizationFilters) => Promise<void>;
  createOrganization: (data: OrganizationCreate) => Promise<OrganizationEntity>;
  updateOrganization: (id: string, data: OrganizationUpdate) => Promise<OrganizationEntity>;
  deleteOrganization: (id: string) => Promise<void>;

  // Utility functions
  refreshOrganizations: () => Promise<void>;
  getOrganizationById: (id: string) => OrganizationEntity | undefined;
  getOrganizationsByType: (type: OrganizationType) => OrganizationEntity[];
  getChildren: (parentId: string) => OrganizationEntity[];
  searchOrganizations: (query: string) => OrganizationEntity[];

  // Filters state
  filters: OrganizationFilters;
  setFilters: (filters: OrganizationFilters) => void;
  clearFilters: () => void;
}

/**
 * Custom hook for Organization Management
 * Handles all CRUD operations and state for 8-layer Vietnamese F&B hierarchy
 */
export const useOrganizationManagement = (
  initialFilters?: OrganizationFilters
): UseOrganizationManagementReturn => {
  // State
  const [state, setState] = useState<OrganizationManagementState>({
    organizations: [],
    loading: false,
    error: null,
    totalCount: 0,
  });

  const [filters, setFiltersState] = useState<OrganizationFilters>(initialFilters || {});

  /**
   * Fetch organizations from API
   */
  const fetchOrganizations = useCallback(async (customFilters?: OrganizationFilters) => {
    setState(prev => ({ ...prev, loading: true, error: null }));

    try {
      const appliedFilters = customFilters || filters;
      const data = await organizationManagementApi.listOrganizations(appliedFilters);

      setState(prev => ({
        ...prev,
        organizations: data,
        totalCount: data.length,
        loading: false,
      }));
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Không thể tải danh sách tổ chức';
      setState(prev => ({
        ...prev,
        error: errorMessage,
        loading: false,
      }));
    }
  }, [filters]);

  /**
   * Create new organization
   */
  const createOrganization = useCallback(async (data: OrganizationCreate): Promise<OrganizationEntity> => {
    setState(prev => ({ ...prev, loading: true, error: null }));

    try {
      const newOrg = await organizationManagementApi.createOrganization(data);

      setState(prev => ({
        ...prev,
        organizations: [...prev.organizations, newOrg],
        totalCount: prev.totalCount + 1,
        loading: false,
      }));

      return newOrg;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Không thể tạo tổ chức mới';
      setState(prev => ({ ...prev, error: errorMessage, loading: false }));
      throw error;
    }
  }, []);

  /**
   * Update existing organization
   */
  const updateOrganization = useCallback(async (
    id: string,
    data: OrganizationUpdate
  ): Promise<OrganizationEntity> => {
    setState(prev => ({ ...prev, loading: true, error: null }));

    try {
      const updatedOrg = await organizationManagementApi.updateOrganization(id, data);

      setState(prev => ({
        ...prev,
        organizations: prev.organizations.map(org =>
          org.id === id ? updatedOrg : org
        ),
        loading: false,
      }));

      return updatedOrg;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Không thể cập nhật tổ chức';
      setState(prev => ({ ...prev, error: errorMessage, loading: false }));
      throw error;
    }
  }, []);

  /**
   * Delete organization (soft delete)
   */
  const deleteOrganization = useCallback(async (id: string): Promise<void> => {
    setState(prev => ({ ...prev, loading: true, error: null }));

    try {
      await organizationManagementApi.deleteOrganization(id);

      setState(prev => ({
        ...prev,
        organizations: prev.organizations.filter(org => org.id !== id),
        totalCount: prev.totalCount - 1,
        loading: false,
      }));
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Không thể xóa tổ chức';
      setState(prev => ({ ...prev, error: errorMessage, loading: false }));
      throw error;
    }
  }, []);

  /**
   * Refresh organizations
   */
  const refreshOrganizations = useCallback(async () => {
    await fetchOrganizations();
  }, [fetchOrganizations]);

  /**
   * Get organization by ID
   */
  const getOrganizationById = useCallback((id: string): OrganizationEntity | undefined => {
    return state.organizations.find(org => org.id === id);
  }, [state.organizations]);

  /**
   * Get organizations by type
   */
  const getOrganizationsByType = useCallback((type: OrganizationType): OrganizationEntity[] => {
    return state.organizations.filter(org => org.organization_type === type);
  }, [state.organizations]);

  /**
   * Get children of a parent organization
   */
  const getChildren = useCallback((parentId: string): OrganizationEntity[] => {
    return state.organizations.filter(org => org.parent_id === parentId);
  }, [state.organizations]);

  /**
   * Search organizations by name or code (local filter)
   */
  const searchOrganizations = useCallback((query: string): OrganizationEntity[] => {
    if (!query.trim()) return state.organizations;

    const lowerQuery = query.toLowerCase();
    return state.organizations.filter(org =>
      org.name.toLowerCase().includes(lowerQuery) ||
      org.code.toLowerCase().includes(lowerQuery)
    );
  }, [state.organizations]);

  /**
   * Set filters and trigger fetch
   */
  const setFilters = useCallback((newFilters: OrganizationFilters) => {
    setFiltersState(newFilters);
  }, []);

  /**
   * Clear all filters
   */
  const clearFilters = useCallback(() => {
    setFiltersState({});
  }, []);

  // Single useEffect: covers both initial mount (filters = {}) and filter changes.
  // ISSUE-033 fix: removed separate mount-only useEffect — it caused a double fetch
  // race condition where the unfiltered result could overwrite a filtered result.
  useEffect(() => {
    fetchOrganizations(filters);
  }, [filters, fetchOrganizations]);

  return {
    // State
    organizations: state.organizations,
    loading: state.loading,
    error: state.error,
    totalCount: state.totalCount,

    // CRUD operations
    fetchOrganizations,
    createOrganization,
    updateOrganization,
    deleteOrganization,

    // Utility functions
    refreshOrganizations,
    getOrganizationById,
    getOrganizationsByType,
    getChildren,
    searchOrganizations,

    // Filters
    filters,
    setFilters,
    clearFilters,
  };
};

export default useOrganizationManagement;
